import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { COLORS, SPACING, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const looksLikeMarkdown = (text) => {
  if (typeof text !== 'string' || !text.trim()) {
    return false;
  }

  const sample = text.slice(0, 10000);
  const markdownPatterns = [
    /^\s{0,3}#{1,6}\s+/m,
    /^\s{0,3}[-*+]\s+/m,
    /^\s{0,3}\d+\.\s+/m,
    /^\s{0,3}>\s+/m,
    /```[\s\S]*?```/m,
    /^\|.+\|\s*$/m,
    /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/m,
  ];

  return markdownPatterns.some((pattern) => pattern.test(sample));
};

// renderMode: 'auto' (default) | 'text' (force plain) | 'markdown' (force rendered)
const DocumentTextarea = ({ value, placeholder = "Document text will appear here...", readOnly = true, style = {}, renderMode = 'auto' }) => {
  const textValue = typeof value === 'string' ? value : '';
  const shouldRenderMarkdown =
    renderMode === 'markdown' ||
    (renderMode === 'auto' && readOnly && looksLikeMarkdown(textValue));

  if (shouldRenderMarkdown) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          minWidth: 0,
          maxWidth: '100%',
          padding: SPACING.md,
          border: `1px solid ${COLORS.border}`,
          borderRadius: BORDER_RADIUS.md,
          backgroundColor: COLORS.white,
          color: COLORS.text,
          boxShadow: SHADOWS.sm,
          overflowX: 'auto',
          overflowY: 'auto',
          lineHeight: '1.6',
          fontSize: '14px',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          ...style,
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            table: ({ children, ...props }) => (
              <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                <table
                  {...props}
                  style={{ borderCollapse: 'collapse', margin: `${SPACING.sm}px 0`, minWidth: 'max-content' }}
                >
                  {children}
                </table>
              </div>
            ),
            th: ({ children, ...props }) => (
              <th
                {...props}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  padding: `${SPACING.xs}px ${SPACING.sm}px`,
                  backgroundColor: COLORS.background,
                  textAlign: 'left',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {children}
              </th>
            ),
            td: ({ children, ...props }) => (
              <td
                {...props}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  padding: `${SPACING.xs}px ${SPACING.sm}px`,
                  verticalAlign: 'top',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {children}
              </td>
            ),
            pre: ({ children }) => (
              <pre
                style={{
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: BORDER_RADIUS.sm,
                  padding: SPACING.sm,
                  maxWidth: '100%',
                  overflowX: 'auto',
                  whiteSpace: 'pre',
                }}
              >
                {children}
              </pre>
            ),
            code: ({ inline, children }) => (
              inline ? (
                <code
                  style={{
                    backgroundColor: COLORS.background,
                    borderRadius: BORDER_RADIUS.sm,
                    padding: '2px 6px',
                    fontSize: '12px',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {children}
                </code>
              ) : (
                <code style={{ fontSize: '12px', whiteSpace: 'pre' }}>
                  {children}
                </code>
              )
            ),
            p: ({ children }) => <p style={{ margin: `${SPACING.xs}px 0 ${SPACING.sm}px 0` }}>{children}</p>,
            h1: ({ children }) => <h1 style={{ margin: `${SPACING.sm}px 0`, fontSize: '1.5rem' }}>{children}</h1>,
            h2: ({ children }) => <h2 style={{ margin: `${SPACING.sm}px 0`, fontSize: '1.3rem' }}>{children}</h2>,
            h3: ({ children }) => <h3 style={{ margin: `${SPACING.sm}px 0`, fontSize: '1.1rem' }}>{children}</h3>,
            a: ({ href, children }) => (
              <a href={href} target="_blank" rel="noreferrer" style={{ color: COLORS.link }}>
                {children}
              </a>
            ),
          }}
        >
          {textValue}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <textarea
      value={textValue}
      readOnly={readOnly}
      placeholder={placeholder}
      style={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        minWidth: 0,
        maxWidth: '100%',
        padding: SPACING.md,
        border: `1px solid ${COLORS.border}`,
        borderRadius: BORDER_RADIUS.md,
        fontFamily: 'inherit', // MDS has no code font: use the MDS text font
        fontSize: '13px',
        lineHeight: '1.8',
        resize: 'none',
        backgroundColor: COLORS.white,
        color: COLORS.text,
        boxShadow: SHADOWS.sm,
        whiteSpace: 'pre',
        overflowX: 'auto',
        overflowY: 'auto',
        wordWrap: 'normal',
        ...style
      }}
    />
  );
};

export default DocumentTextarea;
